import { coverFit, watermarkRect } from "./layout";

export interface CompositeSources {
  /** Camera frame source (a <video>, an <img>, or a 2D canvas already holding the camera image). */
  camera: CanvasImageSource;
  cameraWidth: number;
  cameraHeight: number;
  /** Rendered 3D scene, transparent where empty (the WebGL canvas). */
  scene: CanvasImageSource;
  /** Optional branded watermark. */
  watermark?: HTMLImageElement;
}

export interface CompositeOptions {
  width: number;
  height: number;
  mimeType?: "image/jpeg" | "image/png";
  quality?: number;
  watermarkWidthFraction?: number;
  watermarkMargin?: number;
}

const DEFAULT_WATERMARK_FRACTION = 0.28;

/**
 * The single compositing pipeline both backends converge on: camera (cover-fitted) → 3D scene →
 * watermark, flattened to one canvas and exported. Identical output regardless of tracking path.
 */
export async function composite(sources: CompositeSources, options: CompositeOptions): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = options.width;
  canvas.height = options.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable — cannot composite the capture");

  const crop = coverFit(sources.cameraWidth, sources.cameraHeight, options.width, options.height);
  ctx.drawImage(sources.camera, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, options.width, options.height);

  ctx.drawImage(sources.scene, 0, 0, options.width, options.height);

  if (sources.watermark) {
    const rect = watermarkRect(
      options.width,
      options.height,
      sources.watermark.naturalWidth,
      sources.watermark.naturalHeight,
      {
        widthFraction: options.watermarkWidthFraction ?? DEFAULT_WATERMARK_FRACTION,
        margin: options.watermarkMargin ?? Math.round(options.width * 0.04),
      },
    );
    ctx.drawImage(sources.watermark, rect.x, rect.y, rect.w, rect.h);
  }

  return toBlob(canvas, options.mimeType ?? "image/jpeg", options.quality ?? 0.92);
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("canvas.toBlob returned null"))),
      type,
      quality,
    );
  });
}
